
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationDialogService, NotificationPopupService, UiWindowService } from '../../../../../modules/fwcore';
import { DealGroup, ModelValue, RequestDealGroup, RequestDealSet, RequestModelValue, ResponseDealGroup, ResponseDealSet, ResponseModelValue } from '../../../models';
import { DealsService } from '../../../services';
import { NavigationHelperService } from '../../../services/navigation-helper.service';

@Component({
  selector: 'app-pb-createnew-deal-dlg',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonsModule, DropDownsModule, TextBoxModule, FormsModule, LabelModule],
  templateUrl: './pb-createnew-deal-dlg.component.html',
  styleUrl: './pb-createnew-deal-dlg.component.scss'
})
export class PbCreatenewDealDlgComponent implements OnInit, AfterViewInit, OnDestroy {

  //constants
  protected readonly ctErrorLoadingdealGroupModules = 'An error occurred loading available deal groups.';
  protected readonly ctError = 'error';

  /** Scenario name code */
  protected readonly ctscenarioNameCode = 'dn';
  // -------------- Input/Outputs --------------------------------------------

  protected dealGroupList: DealGroup[] = [];

  /** Two-way dialog visibility */
  public show = model<boolean>(false);

  //-------------------------- Internal properties  --------------------
  public currCompanyId = model<string | null>(null);

  /** User group code filter */
  public filterUserGroupCode = input<string | null>(null);

  protected dealGroupId?: DealGroup;
  protected dealName: string = "";

  /** Subject for component destruction cleanup */
  protected destroy$ = new Subject();

  /** Maximum length for scenario names */
  protected ctscenarioNameMaxlength: number | null = null;

  //--------------------------- Constructor  --------------------------

  constructor(protected afUiWin: UiWindowService
    , protected dealService: DealsService
    , protected notification: NotificationPopupService
    , protected router: Router
    , protected dialogNotification: NotificationDialogService
    , protected navigationHelper: NavigationHelperService) {
    this.getDealGroupModules();
  }

  //--------------------------- ng  -----------------------------------

  ngOnInit(): void {


  }

  ngAfterViewInit(): void {


  }



  /**
  * Cleans up subscriptions when the component is destroyed.
  */
  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/


  protected onCancel(): void {

    this.show.set(false);
  }

  protected onSave(): void {

    this.show.set(false);
    this.createDeal(this.dealName);
  }

  protected getDealGroupModules() {
    const reqdealGroupModule: RequestDealGroup = {

    };

    this.dealService.getData$<RequestDealGroup, ResponseDealGroup>(reqdealGroupModule, this.dealService.endPointDealGroupModule)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {

        if (resp.success === true) {

          this.dealGroupList = resp.data?.map(item => ({
            ...item
            , id: item.id
            , name: item.name ?? ""
          })) ?? [];

        }
        else {
          this.notification.showNotification(resp.message ?? this.ctErrorLoadingdealGroupModules, this.ctError);
        }
      });
  }


  /**
  * Creates a new deal with the specified name
  */
  public createDeal(dealName: string): void {

    const filterCompany = this.currCompanyId();
    const dealGroup = this.dealGroupId;
    const filterUserGroupCode = this.filterUserGroupCode();

    const req: RequestDealSet = {
      userCompanyId: filterCompany
      , userGroupCode: filterUserGroupCode
    };
    if (dealGroup) {
      req.data = [{
        id: null
        , name: dealName
        , dealGroupId: dealGroup.id
      }];

      this.dealService.setData$<RequestDealSet, ResponseDealSet>(req, this.dealService.endPointDeals)
        .pipe(
          takeUntil(this.destroy$)
          , finalize(() => { })
        )
        .subscribe((resp) => {

          if (resp.success !== true) {
            this.dialogNotification.openNotificationDialog("Scenario", resp.message ?? "An error occurred saving the data.");
          }
          else {

            const dealId = (resp.data?.length ? resp.data[0].id : null);

            if (dealId) {

              this.navigationHelper.navigateToScenario(dealGroup.id, dealId);

            }
            else {
              this.dialogNotification.openNotificationDialog("Scenario", "Failed to open the new deal.");
            }
          }
        });
    }
  }

  /** True when the scenario name currently contains a slash character. */
  protected hasSlashInName(): boolean {
    return /[\/\\]/.test(this.dealName || '');
  }

  /**
   * Handles the change event for the deal group selection.
   * @param event Deal group selection change event containing the selected deal group information
   */
  onDealGroupChange(event: any) {
    const dealGroupId = event.id;
    this.ctscenarioNameMaxlength = null; // Reset maxlength when deal group changes
    this.getModelValue$(dealGroupId);
  }

  /**
   * Gets the model value for the current deal group and extracts the max length for the CTScenarioName value to enforce it in the UI when creating or copying deals.
   * @returns max length for the CTScenarioName value or null if not found
   * @param dealGroupId The ID of the deal group for which to retrieve the model value
   */
  protected getModelValue$(dealGroupId?: string | undefined): any {

    const req: RequestModelValue = {};

    req.dealGroupId = dealGroupId;

    return this.dealService.getData$<RequestModelValue, ResponseModelValue>(req, this.dealService.endPointModelValue)
      .pipe(
        takeUntil(this.destroy$)
        , finalize(() => {
        })
      ).subscribe({
        next: (resp) => {
          // Update the maximum length for the scenario name based on the retrieved model value
          const dndata: ModelValue[] | undefined = resp.data?.filter((x: any) => x.code === this.ctscenarioNameCode);
          this.ctscenarioNameMaxlength = dndata && dndata.length > 0 ? Number(dndata[0].max) : null;

        }
      });
  }

}
